import { Component, computed, HostListener, inject, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  CheckboxComponent,
  IconComponent,
  InputComponent,
  SelectComponent,
  TooltipComponent,
} from '@purolator/ui-kit-angular';
import type { PackageSelectionConstraints } from '../../../../core/api/product-master/models/package-selection.model';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import { AlertToast } from '../../../../shared/components/alert-toast/alert-toast';
import { customerPieceFieldHasError } from '../../customer-piece-field';
import { expressPackMaxWeightForUnit } from '../../forms/package-selection.form';
import { PackageMeasureInputDirective } from '../../package-measure-input.directive';
import {
  isExpressBox,
  isExpressEnvelope,
  isExpressPack,
  type PackageUnitSystem,
} from '../../package-types.config';

/**
 * Express packaging expand panel for Envelope, Pack, and Box.
 * Envelope shows the overweight disclaimer only; Pack/Box add total weight and unit;
 * Box also exposes the Additional Handling checkbox.
 */
@Component({
  selector: 'app-express-packaging-details',
  imports: [
    ReactiveFormsModule,
    AlertToast,
    CheckboxComponent,
    IconComponent,
    InputComponent,
    PackageMeasureInputDirective,
    SelectComponent,
    TooltipComponent,
    TranslatePipe,
  ],
  templateUrl: './express-packaging-details.html',
  styleUrl: './express-packaging-details.scss',
})
export class ExpressPackagingDetails {
  private readonly translation = inject(TranslationService);

  /** Bumped on weight blur so the banner re-reads control.touched. */
  private readonly fieldInteractionVersion = signal(0);

  readonly packageTypeCode = input.required<string>();
  readonly constraints = input.required<PackageSelectionConstraints>();
  readonly dimensions = input<string | undefined>(undefined);
  readonly idealFor = input<readonly string[] | undefined>(undefined);
  readonly expressTotalWeight = input.required<FormControl<number | null>>();
  readonly expressRequiresAdditionalHandling = input<FormControl<boolean> | null>(null);
  readonly unitSystem = input<PackageUnitSystem>('imperial');
  readonly showValidationErrors = input(false);
  readonly unitSystemChange = output<PackageUnitSystem>();

  protected readonly isEnvelope = computed(() => isExpressEnvelope(this.packageTypeCode()));

  protected readonly isPack = computed(() => isExpressPack(this.packageTypeCode()));

  protected readonly isBox = computed(() => isExpressBox(this.packageTypeCode()));

  /** Unit labels for the Kit select. */
  protected readonly weightUnitOptions = computed(() => {
    return [
      {
        value: 'lb',
        label: 'Lb',
      },
      {
        value: 'kg',
        label: 'Kg',
      },
    ];
  });

  protected readonly weightUnitValue = computed(() =>
    this.unitSystem() === 'metric' ? 'kg' : 'lb',
  );

  /**
   * Maximum weight from Product Master constraints for the selected Express type (lb).
   * Null when Product Master omits `constraints.weight.max`.
   */
  protected maxWeightLb(): number | null {
    const max = this.constraints().weight.max;
    return typeof max === 'number' && Number.isFinite(max) ? max : null;
  }

  /**
   * Whether the Express weight error banner should show after blur or Save validation.
   * Pack/Box use banner-only errors — never puro-input field error styling.
   */
  protected hasWeightError(): boolean {
    this.fieldInteractionVersion();
    return customerPieceFieldHasError(this.expressTotalWeight(), this.showValidationErrors());
  }

  /**
   * Marks the Express weight field touched on blur so the banner can show without Save.
   */
  protected onWeightBlur(): void {
    this.expressTotalWeight().markAsTouched();
    this.fieldInteractionVersion.update((version) => version + 1);
  }

  /**
   * Resolves the i18n key for the Express weight error banner.
   */
  protected weightErrorKey(): string {
    const errors = this.expressTotalWeight().errors;
    if (!errors) {
      return '';
    }

    if (errors['required'] || errors['min']) {
      return 'createShipment.packageStep.express.weightRequired';
    }

    if (errors['max']) {
      return 'createShipment.packageStep.express.weightMaxExceededDual';
    }

    if (errors['expressWeightDecimals']) {
      return 'createShipment.packageStep.express.weightDecimalsInvalid';
    }

    if (errors['expressWeightInvalid']) {
      return 'createShipment.packageStep.express.weightInvalid';
    }

    return 'createShipment.packageStep.express.weightRequired';
  }

  /**
   * Params for the Express weight error banner (dual-unit max from Product Master).
   * Empty when Product Master omits `constraints.weight.max`.
   */
  protected weightErrorParams(): Record<string, string> {
    const maxLb = this.maxWeightLb();
    if (maxLb === null) {
      return {};
    }

    const maxKg = expressPackMaxWeightForUnit('metric', maxLb);
    return { maxLb: String(maxLb), maxKg: String(maxKg) };
  }

  /**
   * Emits imperial/metric when the Pack/Box lb/kg control changes.
   * Keeps the typed weight value as-is (no auto-conversion).
   */
  protected onWeightUnitChange(value: string | number | (string | number)[]): void {
    const raw = Array.isArray(value) ? value[0] : value;
    const unit = String(raw ?? '');
    this.unitSystemChange.emit(unit === 'kg' ? 'metric' : 'imperial');
  }

  /**
   * Keeps the kit unit menu open through option mousedown so click can select.
   * puro-select options are non-focusable; without this, combobox blur closes the
   * menu before click (breaks Figma alternate-only lb/kg switching).
   */
  @HostListener('mousedown', ['$event'])
  protected preventUnitOptionBlur(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.puro-select__option')) {
      event.preventDefault();
    }
  }
}
